import dayjs from 'dayjs';
import type { BOQHeader, BOQLineItem } from '@/services/boq.service';
import type { SimplePrintLine } from '@/print-engine/adapters/simple-document';
import { formatMoney } from '@/utils/format';

function toNum(v: unknown): number {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export function formatBoqPrintAmount(amount: unknown, currency: string): string {
  return formatMoney(toNum(amount), {
    currency: currency || 'AED',
    locale: 'en-US',
    display: 'code_prefix',
    separator: ' ',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function categoryNameMap(boq: BOQHeader): Map<string, string> {
  return new Map((boq.categories ?? []).map((c) => [c.id, c.category_name]));
}

function buildLineDetailExtraLines(line: BOQLineItem, currency: string, categoryLabel?: string): string[] {
  const out: string[] = [];
  if (categoryLabel) out.push(`Category: ${categoryLabel}`);
  if (line.description?.trim()) out.push(`Description: ${line.description.trim()}`);
  if (line.revision?.trim()) out.push(`Revision: ${line.revision.trim()}`);

  const materials = line.materials ?? [];
  if (materials.length) {
    out.push('Materials:');
    for (const m of materials) {
      out.push(
        `  • ${m.description}: ${m.quantity} ${m.uom} @ ${formatBoqPrintAmount(m.unit_cost, currency)} = ${formatBoqPrintAmount(m.total_cost, currency)}`
      );
      if (m.supplier_name?.trim()) out.push(`    Supplier: ${m.supplier_name.trim()}`);
    }
  }

  const operations = [...(line.operations ?? [])].sort(
    (a, b) => (a.sequence_number ?? 0) - (b.sequence_number ?? 0)
  );
  if (operations.length) {
    out.push('Operations:');
    for (const op of operations) {
      const outsourced = op.is_outsourced
        ? ` [Outsourced${op.outsource_cost != null ? ` ${formatBoqPrintAmount(op.outsource_cost, currency)}` : ''}]`
        : '';
      out.push(
        `  • #${op.sequence_number} ${op.description}: setup ${op.setup_time_hours}h + run ${op.run_time_per_unit_hours}h/unit${outsourced} = ${formatBoqPrintAmount(op.total_cost, currency)}`
      );
      const scope = op.is_outsourced ? op.outsourced_scope_detail?.trim() : '';
      if (scope) {
        out.push(`    Scope: ${scope}`);
      }
    }
  }

  const breakdown: string[] = [];
  if (toNum(line.material_cost) > 0) breakdown.push(`Material ${formatBoqPrintAmount(line.material_cost, currency)}`);
  if (toNum(line.labor_cost) > 0) breakdown.push(`Labor ${formatBoqPrintAmount(line.labor_cost, currency)}`);
  if (toNum(line.overhead_cost) > 0) breakdown.push(`Overhead ${formatBoqPrintAmount(line.overhead_cost, currency)}`);
  if (toNum(line.machine_cost) > 0) breakdown.push(`Machine ${formatBoqPrintAmount(line.machine_cost, currency)}`);
  if (toNum(line.tooling_cost) > 0) breakdown.push(`Tooling ${formatBoqPrintAmount(line.tooling_cost, currency)}`);
  if (toNum(line.operation_outsourced_cost) > 0) {
    breakdown.push(`Outsource ${formatBoqPrintAmount(line.operation_outsourced_cost, currency)}`);
  }
  if (breakdown.length) out.push(`Cost breakdown: ${breakdown.join(' · ')}`);

  if (line.margin_percentage != null) {
    out.push(`Line margin: ${Number(line.margin_percentage).toFixed(2)}%`);
  }
  if (line.use_manual_cost) out.push('Cost mode: manual unit cost override');
  if (line.notes?.trim()) out.push(`Notes: ${line.notes.trim()}`);

  return out;
}

export function buildBoqPrintLines(boq: BOQHeader): SimplePrintLine[] {
  const currency = boq.currency || 'AED';
  const categories = categoryNameMap(boq);
  const items = [...(boq.lineItems ?? [])].sort((a, b) => (a.line_number ?? 0) - (b.line_number ?? 0));

  return items.map((line, idx) => {
    const categoryLabel = line.category_id ? categories.get(line.category_id) : undefined;
    const title = [line.item_code, line.item_name].filter(Boolean).join(' — ') || '—';
    return {
      lineNumber: line.line_number ?? idx + 1,
      description: title,
      cells: {
        qty: String(line.quantity),
        uom: line.uom || '—',
        unitCost: formatBoqPrintAmount(line.unit_cost, currency),
        unitSell: formatBoqPrintAmount(line.unit_selling_price, currency),
        lineCost: formatBoqPrintAmount(line.line_total, currency),
        lineSell: formatBoqPrintAmount(line.line_selling_total, currency),
      },
      extraLines: buildLineDetailExtraLines(line, currency, categoryLabel),
    };
  });
}

export function buildBoqPrintMetaLines(boq: BOQHeader): Array<{ label: string; value: string }> {
  const currency = boq.currency || 'AED';
  const costTotal = toNum(boq.total_amount);
  const sellTotal = toNum(boq.selling_price);
  const marginAmt = sellTotal - costTotal;
  const marginPct = toNum(boq.margin_percentage);

  return [
    { label: 'BOQ name', value: boq.boq_name || '' },
    { label: 'Customer', value: boq.customer_name || '' },
    { label: 'Project', value: boq.project_name || '' },
    {
      label: 'Opportunity',
      value: boq.opportunity
        ? `${boq.opportunity.opportunity_number} — ${boq.opportunity.opportunity_name}`
        : '',
    },
    { label: 'Status', value: boq.status || '' },
    { label: 'Currency', value: currency },
    {
      label: 'BOQ date',
      value: boq.boq_date ? dayjs(boq.boq_date).format('DD MMM YYYY') : '',
    },
    {
      label: 'Valid until',
      value: boq.valid_until_date ? dayjs(boq.valid_until_date).format('DD MMM YYYY') : '',
    },
    { label: 'Sales team', value: boq.sales_team_name || '' },
    { label: 'Total cost', value: formatBoqPrintAmount(costTotal, currency) },
    {
      label: 'Margin',
      value:
        marginPct > 0
          ? `${marginPct.toFixed(2)}% (${formatBoqPrintAmount(marginAmt, currency)})`
          : formatBoqPrintAmount(marginAmt, currency),
    },
    { label: 'Selling price', value: formatBoqPrintAmount(sellTotal, currency) },
    { label: 'Line items', value: String(boq.lineItems?.length ?? 0) },
  ].filter((r) => r.value);
}
